<template>
    <div class="automation-action-settings">
        <div class="action-row" v-if="value.alias">
            <div class="drag-handler handle"></div>
            <span class="action-type">
                {{ dynamicAction.title }}
                <strong v-if="needsAlias">({{ value.alias }})</strong>
            </span>
            <span class="action-actions">
                <button class="btn btn-primary" @click.prevent="openModal()">
                    <span class="fa fa-pencil"></span>
                </button>
                <button
                    class="btn btn-danger"
                    @click.prevent="confirmDelete(id)"
                >
                    <span class="fa fa-remove"></span>
                </button>
            </span>
        </div>
        <modal
            ref="actionForm"
            :startsOpen="false"
            :id="'actionForm' + id"
            @close="cancel"
            v-bind:class="modalClasses"
        >
            <template slot="title">
                {{ dynamicAction.title }} <br />
                <small>{{ dynamicAction.description }}</small>
            </template>
            <template slot="body">
                <div>
                    <form ref="form">
                        <div class="field" v-if="needsAlias">
                            <label
                                v-bind:class="{ 'label-danger': !isAliasValid }"
                                >{{ t('automations-form-action_modal-identification_label') }}</label
                            >
                            <small
                                >{{ t('automations-form-action_modal-identification_help') }}</small
                            >
                            <input
                                type="text"
                                v-model="action.alias"
                                class="form-control"
                                maxlength="25"
                            />
                            <small
                                class="error-message text-danger"
                                v-if="action.alias && action.alias.length > 25"
                                >{{ t('automations-form-action_modal-identification_error') }}</small
                            >
                            <small class="label-danger" v-if="!isAliasValid"
                                >{{ t('automations-form-action_modal-identification_duplicate_error') }}</small
                            >
                        </div>
                        <component
                            :is="'automation-action-' + type"
                            :properties="action.properties"
                            :conditions="action.conditions"
                            @propertiesUpdated="propertiesUpdated"
                            @conditionsUpdated="conditionsUpdated"
                            @mounted="actionMounted"
                            :actionId="id"
                            ref="dynamicAction"
                        />
                    </form>
                </div>
            </template>
            <template slot="footer">
                <div>
                    <button
                        type="button"
                        class="btn btn-cancel"
                        @click="cancel"
                    >
                        {{ t('automations-form-action_modal-cancel_button') }}
                    </button>
                    <button
                        type="button"
                        class="btn btn-success"
                        @click="add"
                        :disabled="!isValid()"
                    >
                        {{ t('automations-form-action_modal-add_button') }}
                    </button>
                </div>
            </template>
        </modal>
    </div>
</template>
<script>
import languages from "../../languages";
import notification from "./notification/notification";
import coupon from "./coupon/coupon";
import cashback from "./cashback/cashback";
import couponExternal from "./coupon/coupon_external";
import feedbackLink from "./feedback_link/feedback_link";
import addPoints from "./add_points/add_points";
import authLink from "./auth_link/auth_link";
import task from "./task/task";
import updateField from "./update_field/update_field";
import webHook from "./web_hook/web_hook";
import AiContent from "./ai_content/ai_content";
import MoveClientStage from "./move_client_stage/move_client_stage";
import AddToSegment from "./add_to_segment/add_to_segment";
import RemoveFromSegment from "./remove_from_segment/remove_from_segment";
import PluginAction from "./plugin/plugin_action";
import SmartInsertion from "./smart_insertion/smart_insertion";
import RemoveSmartInsertion from "./smart_insertion/remove_smart_insertion";
import DataSets from "./data_sets/data_sets";
import wait from "./wait/wait";
import Calculator from "./calculator/calculator";

export default {
    props: {
        type: String,
        value: {},
        id: String,
    },
    components: {
        "automation-action-notification": notification,
        "automation-action-coupon": coupon,
        "automation-action-cashback": cashback,
        "automation-action-coupon_external": couponExternal,
        "automation-action-feedback_link": feedbackLink,
        "automation-action-add_points": addPoints,
        "automation-action-auth_link": authLink,
        "automation-action-task": task,
        "automation-action-update_field": updateField,
        "automation-action-web_hook": webHook,
        "automation-action-ai_content": AiContent,
        "automation-action-move_client_stage": MoveClientStage,
        "automation-action-add_to_segment": AddToSegment,
        "automation-action-remove_from_segment": RemoveFromSegment,
        "automation-action-plugin_action": PluginAction,
        "automation-action-smart_insertion": SmartInsertion,
        "automation-action-remove_smart_insertion": RemoveSmartInsertion,
        "automation-action-data_sets": DataSets,
        "automation-action-wait": wait,
        "automation-action-calculator": Calculator,
    },
    data: function () {
        let action = { ...this.value };
        action.properties = { ...this.value.properties };
        action.conditions = [...this.value.conditions];

        if (!action.alias) {
            let key = "automations-action_alias-" + this.type;
            action.alias = this.t(key);
        }
        this.action = action;
        return {
            action: action,
            dynamicAction: {
                title: "",
                description: "",
            },
        };
    },
    mounted: function () {
        let action = { ...this.value };
        action.properties = { ...this.value.properties };
        action.conditions = [...this.value.conditions];

        if (!action.alias) {
            let key = "automations-action_alias-" + this.type;
            action.alias = this.t(key);
        }
        if (!this.needsAlias) {
            action.alias += "_" + this.id;
        }
        this.action = action;
    },
    computed: {
        title: function () {
            return this.$refs.dynamicAction
                ? this.$refs.dynamicAction.title
                : "";
        },
        actions: function () {
            return this.$store.state.automationForm.actions;
        },
        actionsAliases: function () {
            return this.actions.map((action) => action.form.alias);
        },
        modalClasses: function () {
            const classes = ["modal-action-" + this.type];
            if (
                this.type === "notification" ||
                this.type === "smart_insertion"
            ) {
                classes.push("modal-wide");
            }
            return classes;
        },
        isAliasValid: function () {
            let idx = this.actionsAliases.indexOf(this.action.alias);
            if (
                this.action.alias &&
                this.action.alias.length <= 25 &&
                (idx === -1 || parseInt(this.id) === parseInt(idx))
            ) {
                return true;
            }
            return false;
        },
        needsAlias: function () {
            return !(this.type === "add_points" || this.type === "wait");
        },
    },
    methods: {
        changed: function () {
            this.$emit("input", {});
        },
        actionMounted: function (data) {
            this.dynamicAction = data;
        },
        propertiesUpdated: function (properties) {
            this.action.properties = properties;
        },
        conditionsUpdated: function (conditions) {
            this.action.conditions = conditions;
        },
        openModal: function () {
            if (this.$refs.actionForm) {
                this.$refs.actionForm.open();
            }
        },
        isValid: function () {
            return this.dynamicAction && this.dynamicAction.isValid
                ? this.action.alias &&
                      this.isAliasValid &&
                      this.dynamicAction.isValid()
                : false;
        },
        closeModal: function () {
            this.$emit("closed");
            this.$refs.actionForm.toggleShow(false);
        },
        cancel: function () {
            window.setTimeout(() => {
                let action = { ...this.value };
                action.properties = { ...this.value.properties };
                action.conditions = [...this.value.conditions];
                this.action = action;
                window.setTimeout(() => {
                    if (!this.isValid()) {
                        this.$emit("deleted");
                    }
                    this.closeModal();
                }, 100);
            }, 100);
        },
        add: function () {
            if (this.$refs.form.checkValidity()) {
                this.$emit("input", { ...this.action });
                this.closeModal();
            } else {

                this.$refs.form.reportValidity();
            }
        },
        confirmDelete(actionId) {
            this.$emit("confirm-delete", actionId);
        },
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
    },
};
</script>
